import { YouTubeVideo, YouTubeLiveStream } from '../types/youtube';

const YOUTUBE_UPLOADS_PLAYLIST_ID = 'UURJmbYsGBiYPioIUuBdopAQ';

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || '';

/** Shared feed cache — keeps total API cost at 2 units per refresh (not 100+). */
const FEED_CACHE_TTL_MS = 60 * 60 * 1000;
const QUOTA_BACKOFF_MS = 60 * 60 * 1000;

type SermonFeed = {
  videos: YouTubeVideo[];
  liveStream: YouTubeLiveStream | null;
};

type FeedCacheEntry = SermonFeed & {
  expiresAt: number;
};

let feedCache: FeedCacheEntry | null = null;
let quotaBlockedUntil = 0;
let feedInFlight: Promise<SermonFeed> | null = null;

function isQuotaBlocked(): boolean {
  return Date.now() < quotaBlockedUntil;
}

function markQuotaBlocked(reason: string): void {
  quotaBlockedUntil = Date.now() + QUOTA_BACKOFF_MS;
  console.warn(`YouTube Data API quota blocked (${reason}). Using cache for up to 1 hour.`);
}

function getFreshFeedCache(): SermonFeed | null {
  if (!feedCache || Date.now() > feedCache.expiresAt) {
    return null;
  }

  return {
    videos: feedCache.videos,
    liveStream: feedCache.liveStream,
  };
}

function getStaleFeedCache(): SermonFeed | null {
  if (!feedCache) {
    return null;
  }

  return {
    videos: feedCache.videos,
    liveStream: feedCache.liveStream,
  };
}

function setFeedCache(feed: SermonFeed): void {
  feedCache = {
    ...feed,
    expiresAt: Date.now() + FEED_CACHE_TTL_MS,
  };
}

async function readYouTubeError(response: Response): Promise<string | null> {
  try {
    const data = await response.json();
    const message = data?.error?.message as string | undefined;
    return message ?? null;
  } catch {
    return null;
  }
}

async function youtubeFetch(url: string): Promise<Response | null> {
  if (!YOUTUBE_API_KEY || isQuotaBlocked()) {
    return null;
  }

  try {
    const response = await fetch(url);

    if (response.status === 429) {
      markQuotaBlocked('429');
      return null;
    }

    if (response.status === 403) {
      const message = await readYouTubeError(response);
      if (message?.toLowerCase().includes('quota')) {
        markQuotaBlocked('quota exceeded');
      }
      return null;
    }

    if (!response.ok) {
      return null;
    }

    return response;
  } catch (error) {
    console.error('YouTube API request failed:', error);
    return null;
  }
}

function findLiveStream(
  videos: YouTubeVideo[],
  liveStatusById: Map<string, string | undefined>
): YouTubeLiveStream | null {
  for (const video of videos) {
    if (liveStatusById.get(video.videoId) === 'live') {
      return {
        videoId: video.videoId,
        title: video.title,
        thumbnail: video.thumbnail,
        isLive: true,
      };
    }
  }

  return null;
}

/**
 * Refreshes sermon list + live status in one batch (2 quota units):
 * - playlistItems.list = 1 unit
 * - videos.list = 1 unit
 *
 * Avoid search.list (100 units) — with a 100/day quota, one search exhausts the project.
 */
async function refreshSermonFeedFromApi(): Promise<SermonFeed> {
  const playlistResponse = await youtubeFetch(
    `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${YOUTUBE_UPLOADS_PLAYLIST_ID}&maxResults=10&key=${YOUTUBE_API_KEY}`
  );

  if (!playlistResponse) {
    return getStaleFeedCache() ?? { videos: [], liveStream: null };
  }

  const playlistData = await playlistResponse.json();

  if (!playlistData.items?.length) {
    return getStaleFeedCache() ?? { videos: [], liveStream: null };
  }

  const videos: YouTubeVideo[] = playlistData.items.map((item: any) => ({
    id: item.snippet.resourceId.videoId,
    videoId: item.snippet.resourceId.videoId,
    title: item.snippet.title,
    description: item.snippet.description,
    thumbnail: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.default.url,
    publishedAt: item.snippet.publishedAt,
  }));

  const videoIds = videos.map((video) => video.videoId).join(',');
  const liveStatusById = new Map<string, string | undefined>();

  const detailsResponse = await youtubeFetch(
    `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoIds}&key=${YOUTUBE_API_KEY}`
  );

  if (detailsResponse) {
    const detailsData = await detailsResponse.json();
    for (const item of detailsData.items ?? []) {
      liveStatusById.set(item.id, item.snippet?.liveBroadcastContent);
    }
  }

  const feed: SermonFeed = {
    videos,
    liveStream: findLiveStream(videos, liveStatusById),
  };

  setFeedCache(feed);
  return feed;
}

async function ensureSermonFeed(): Promise<SermonFeed> {
  const cached = getFreshFeedCache();
  if (cached) {
    return cached;
  }

  if (isQuotaBlocked()) {
    return getStaleFeedCache() ?? { videos: [], liveStream: null };
  }

  if (!feedInFlight) {
    feedInFlight = refreshSermonFeedFromApi().finally(() => {
      feedInFlight = null;
    });
  }

  return feedInFlight;
}

export const getChannelVideos = async (): Promise<YouTubeVideo[]> => {
  if (!YOUTUBE_API_KEY) {
    console.warn('YouTube API key not configured. Using embedded channel feed.');
    return [];
  }

  const feed = await ensureSermonFeed();
  return feed.videos;
};

export const checkForLiveStream = async (): Promise<YouTubeLiveStream | null> => {
  if (!YOUTUBE_API_KEY) {
    return null;
  }

  const feed = await ensureSermonFeed();
  return feed.liveStream;
};
